import isEmpty from 'lodash/isEmpty'

import arriereBackoffice from '@avantstay/arriere-clients/dist/arriereBackoffice'

import {
  ManualBookingExistingGuestsProps,
  ManualBookingsCandidatesProps,
} from '@new/__global/components/GuestSearchSelect/GuestSearchSelect.types'
import {
  CreateBookingForExistingGuestProps,
  CreateBookingForNewGuestProps,
  CreateHotelBookingForExistingGuestProps,
  CreateHotelBookingForNewGuestProps,
} from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'

const sortMembersFirst = <T extends { membershipInfo?: unknown }>(a: T, b: T) => {
  return (b.membershipInfo ? 1 : 0) - (a.membershipInfo ? 1 : 0)
}

export default {
  async getManualBookingExistingGuests({ phone, email }: ManualBookingExistingGuestsProps) {
    return arriereBackoffice.queries.manualBookingExistingGuests
      .raw({
        __alias: 'getManualBookingExistingGuests',
        __args: { phone, email },
        total: true,
        results: {
          guestId: true,
          userId: true,
          kind: {
            id: true,
            type: true,
            subType: true,
          },
          name: true,
          emails: true,
          phones: true,
          stays: {
            total: true,
          },
          membershipInfo: {
            planId: true,
            status: true,
            isActive: true,
          },
        },
      })
      .then(({ data, errors }) => {
        if (errors) {
          throw Error(errors[0])
        }

        if (!data) return data

        return {
          ...data,
          results: data.results.toSorted(sortMembersFirst),
        }
      })
  },

  async getManualBookingCandidates({ contactInfoTerm, pagination }: ManualBookingsCandidatesProps) {
    return arriereBackoffice.queries.manualBookingCandidates
      .raw({
        __alias: 'getManualBookingCandidates',
        __args: { contactInfoTerm, pagination },
        total: true,
        paginationParams: { page: true },
        results: {
          guestId: true,
          userId: true,
          kind: {
            id: true,
            type: true,
            subType: true,
          },
          name: true,
          emails: true,
          phones: true,
          stays: {
            total: true,
          },
          membershipInfo: {
            planId: true,
            isActive: true,
            status: true,
          },
        },
      })
      .then(({ data, errors }) => {
        if (errors) {
          throw Error(errors[0])
        }

        if (!data) return data

        return {
          ...data,
          results: data.results.toSorted(sortMembersFirst),
        }
      })
  },

  // Mutations

  async createBookingForExistingGuest({
    bookingRequestInfo,
    guestId,
  }: CreateBookingForExistingGuestProps) {
    if (bookingRequestInfo?.homeId) {
      return arriereBackoffice.mutations.manualBooking
        .raw({
          __alias: 'createBookingForExistingGuest',
          createForExistingGuest: {
            __args: {
              request: { ...bookingRequestInfo, homeId: bookingRequestInfo?.homeId },
              guestId,
            },
            id: true,
            hash: true,
          },
        })
        .then(({ data, errors }) => {
          return {
            data: data?.createForExistingGuest,
            errors,
          }
        })
    }
  },

  async createBookingForNewGuest({ bookingRequestInfo, guestData }: CreateBookingForNewGuestProps) {
    if (bookingRequestInfo?.homeId) {
      return arriereBackoffice.mutations.manualBooking
        .raw({
          __alias: 'createBookingForNewGuest',
          createForNewGuest: {
            __args: {
              request: { ...bookingRequestInfo, homeId: bookingRequestInfo?.homeId },
              guestData,
            },
            hash: true,
            id: true,
            guestId: true,
          },
        })
        .then(({ data, errors }) => {
          return {
            data: data?.createForNewGuest,
            errors,
          }
        })
    }
  },
}

export const createHotelBookingForExistingGuest = async ({
  bookingRequestInfo,
  guestId,
}: CreateHotelBookingForExistingGuestProps) => {
  const hotelRoomTypeId = bookingRequestInfo.hotelRoomTypeId

  if (!hotelRoomTypeId) {
    return Promise.reject(
      new Error('hotelRoomTypeId is required for creating a hotel booking for an existing guest.'),
    )
  }

  const homeId = !isEmpty(bookingRequestInfo.homeId) ? bookingRequestInfo.homeId : undefined

  return arriereBackoffice.mutations.manualBooking
    .raw({
      __alias: 'createHotelBookingForExistingGuest',
      createForHotelAndExistingGuest: {
        __args: { request: { ...bookingRequestInfo, hotelRoomTypeId, homeId }, guestId },
        id: true,
        hash: true,
      },
    })
    .then(({ data, errors }) => {
      return {
        data: data?.createForHotelAndExistingGuest,
        errors,
      }
    })
}

export const createHotelBookingForNewGuest = async ({
  bookingRequestInfo,
  guestData,
}: CreateHotelBookingForNewGuestProps) => {
  const hotelRoomTypeId = bookingRequestInfo.hotelRoomTypeId
  if (!hotelRoomTypeId) {
    return Promise.reject(
      new Error('hotelRoomTypeId is required for creating a hotel booking for an new guest.'),
    )
  }

  const homeId = !isEmpty(bookingRequestInfo.homeId) ? bookingRequestInfo.homeId : undefined

  return arriereBackoffice.mutations.manualBooking
    .raw({
      __alias: 'createHotelBookingForNewGuest',
      createForHotelAndNewGuest: {
        __args: { request: { ...bookingRequestInfo, hotelRoomTypeId, homeId }, guestData },
        hash: true,
        id: true,
        guestId: true,
      },
    })
    .then(({ data, errors }) => {
      return {
        data: data?.createForHotelAndNewGuest,
        errors,
      }
    })
}
